import { useEffect, useState } from 'react';

import { useCoverageShiftAvailability } from '@apps/web/features/coverage/api/shift_availability';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { useInfiniteOrganizationCaregivers } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type WorkforceMapPoint } from '@libraries/typescript/api_client';

type Caregiver = {
    readonly id: string;
    readonly name: string;
};

/** Owns recipient defaults and explicit choices across search, paging and radius changes. */
export function useOutreachCaregivers({
    organizationId,
    caregivers,
    shiftIds,
    availability,
}: {
    readonly organizationId: string;
    readonly caregivers: ReadonlyArray<{
        readonly point: WorkforceMapPoint;
        readonly miles: number;
    }>;
    readonly shiftIds: ReadonlyArray<string>;
    readonly availability: Pick<
        ReturnType<typeof useCoverageShiftAvailability>,
        'statuses' | 'isLoading' | 'isValidating' | 'error' | 'mutate'
    >;
}) {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search.trim(), 200);
    const searching = shiftIds.length > 0 && search.trim().length > 0;
    const searchReady =
        searching &&
        search.trim().length >= 2 &&
        search.trim() === debouncedSearch;
    const roster = useInfiniteOrganizationCaregivers(
        organizationId,
        { reason: 'coverage-outreach-caregiver-search' },
        debouncedSearch,
        {
            enabled: shiftIds.length > 0 && debouncedSearch.length >= 2,
            pageSize: 25,
            excludeSkipListed: true,
        },
    );
    const searchResults =
        searchReady && !roster.hasStaleData && !roster.error
            ? roster.caregivers
            : [];
    const [addedCaregivers, setAddedCaregivers] = useState<
        ReadonlyMap<string, Caregiver>
    >(new Map());
    const shiftsKey = [...shiftIds].sort().join(',');
    const [choices, setChoices] = useState<{
        readonly shiftsKey: string;
        readonly overrides: ReadonlyMap<string, boolean>;
    }>({ shiftsKey, overrides: new Map() });
    useEffect(() => {
        setChoices({ shiftsKey, overrides: new Map() });
        if (!shiftsKey) {
            setSearch('');
        }
    }, [shiftsKey]);
    const overrides =
        choices.shiftsKey === shiftsKey
            ? choices.overrides
            : new Map<string, boolean>();
    const nearbyById = new Map(
        caregivers.map((caregiver) => [
            caregiver.point.caregiver_id,
            caregiver,
        ]),
    );
    const externalIds = [
        ...new Set([
            ...addedCaregivers.keys(),
            ...searchResults.map((caregiver) => caregiver.id),
        ]),
    ].filter((id) => !nearbyById.has(id));
    const externalAvailability = useCoverageShiftAvailability(
        organizationId,
        shiftIds,
        externalIds,
    );
    const candidates = new Map<string, Caregiver>(
        caregivers.map(({ point }) => [
            point.caregiver_id,
            { id: point.caregiver_id, name: point.name },
        ]),
    );
    for (const caregiver of addedCaregivers.values()) {
        if (shiftIds.length > 0 && !candidates.has(caregiver.id)) {
            candidates.set(caregiver.id, caregiver);
        }
    }
    const allRows = [
        ...new Map([
            ...candidates,
            ...searchResults.map(
                (caregiver) => [caregiver.id, caregiver] as const,
            ),
        ]).values(),
    ].map((caregiver) => {
        const nearby = nearbyById.get(caregiver.id);
        const result = nearby ? availability : externalAvailability;
        const status = result.statuses.get(caregiver.id);
        const loading = result.isLoading || result.isValidating;
        const eligible =
            status === 'available' ||
            status === 'unknown' ||
            status === 'unavailable';
        const selected =
            shiftIds.length > 0 &&
            (overrides.get(caregiver.id) ??
                (Boolean(nearby) &&
                    status === 'available' &&
                    !result.isLoading &&
                    !result.error)) &&
            (loading || Boolean(result.error) || eligible);
        return {
            ...caregiver,
            miles: nearby?.miles,
            currentStatus: nearby?.point.availability_status ?? 'unknown',
            status,
            loading,
            error: result.error,
            eligible,
            selected,
        };
    });
    const selectedRows = allRows.filter(
        (row) => row.selected && candidates.has(row.id),
    );
    const rows = searching
        ? allRows.filter((row) =>
              searchResults.some((caregiver) => caregiver.id === row.id),
          )
        : allRows.filter((row) => candidates.has(row.id));
    return {
        search,
        setSearch,
        searching,
        searchLoading:
            searching &&
            search.trim().length >= 2 &&
            (!searchReady || roster.isLoading || roster.hasStaleData),
        searchError: searchReady ? roster.error : undefined,
        retrySearch: () => void roster.mutate(),
        hasMoreSearchResults:
            searchReady &&
            !roster.hasStaleData &&
            !roster.error &&
            !roster.isLoading &&
            !roster.isReachingEnd,
        loadingMore: roster.isLoadingMore,
        loadMoreSearchResults: () => void roster.setSize(roster.size + 1),
        rows,
        selectedIds: selectedRows.map((row) => row.id),
        selectionPending:
            selectedRows.some((row) => row.loading || row.error) ||
            availability.isLoading ||
            availability.isValidating ||
            Boolean(availability.error),
        externalError: externalAvailability.error,
        retryExternalAvailability: () => void externalAvailability.mutate(),
        setSelected: (caregiver: Caregiver, checked: boolean) => {
            setChoices((previous) => {
                const next = new Map(
                    previous.shiftsKey === shiftsKey ? previous.overrides : [],
                );
                next.set(caregiver.id, checked);
                return { shiftsKey, overrides: next };
            });
            if (checked) {
                setAddedCaregivers((previous) =>
                    new Map(previous).set(caregiver.id, {
                        id: caregiver.id,
                        name: caregiver.name,
                    }),
                );
            }
        },
    };
}
